import { type ReactNode } from "react";

export interface Tab {
  id: string;
  label: string;
  badge?: number;
  content: ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (id: string) => void;
}

export const Tabs = ({ tabs, activeTab, onTabChange }: TabsProps) => {
  const activeTab_ = tabs.find((t) => t.id === activeTab);
  const activeLabel = activeTab_
    ? activeTab_.badge && activeTab_.badge > 0
      ? `${activeTab_.label} (${activeTab_.badge > 99 ? "99+" : activeTab_.badge})`
      : activeTab_.label
    : "";

  return (
    <div>
      <div className="sm:hidden border-b border-global-primary pb-2">
        <select
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value)}
          className="w-full rounded-lg border border-global-secondary bg-global-overlay px-3 py-2.5 text-size-xs font-medium text-global-primary appearance-none cursor-pointer"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "1rem",
          }}
          aria-label={`Tab actual: ${activeLabel}`}
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id}>
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0
                ? ` (${tab.badge > 99 ? "99+" : tab.badge})`
                : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="hidden sm:flex gap-1 border-b border-global-primary overflow-x-auto scrollbar-none">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-size-xs font-medium whitespace-nowrap transition-colors duration-200 border-b-2 -mb-px cursor-pointer ${
                isActive
                  ? "text-global-accent border-global-accent"
                  : "text-global-muted border-transparent hover:text-global-primary hover:border-global-secondary"
              }`}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-size-2xs font-semibold transition-colors duration-200 ${
                    isActive
                      ? "btn-global-primary"
                      : "bg-global-overlay-secondary text-global-muted"
                  }`}
                >
                  {tab.badge > 99 ? "99+" : tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="pt-5 sm:pt-6">
        {tabs.find((t) => t.id === activeTab)?.content}
      </div>
    </div>
  );
};
